import { useMemo, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVault } from "@/context/VaultContext";
import { nameOf } from "@/lib/selectors";

export function GroupDialog({ open, onOpenChange }) {
  const { q, version, createGroup, setActiveSid } = useVault();
  const [name, setName] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contacts = useMemo(() => (open ? q("SELECT * FROM contacts ORDER BY alias") : []), [open, version, q]);
  const toggle = (code) => setPicked((p) => (p.includes(code) ? p.filter((c) => c !== code) : [...p, code]));

  const submit = async () => {
    setBusy(true);
    const sid = await createGroup(name.trim(), picked);
    setBusy(false);
    setName(""); setPicked([]);
    onOpenChange(false);
    setActiveSid(sid);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-dialog" className="bg-nebula border-rim text-ash sm:max-w-md rounded-secure">
        <DialogHeader>
          <DialogTitle className="font-branding">New constellation</DialogTitle>
          <DialogDescription className="text-ash/50">Each message is sealed separately for every member.</DialogDescription>
        </DialogHeader>
        <input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name"
          className="w-full bg-void border border-rim rounded-secure px-3 py-2.5 text-sm placeholder-ash/30 focus:outline-none focus:border-stellar/50" />
        <div className="max-h-64 overflow-y-auto space-y-1">
          {contacts.length === 0 && <p className="text-sm text-ash/45 py-6 text-center">Connect with contacts first.</p>}
          {contacts.map((c) => {
            const on = picked.includes(c.code);
            return (
              <button key={c.code} onClick={() => toggle(c.code)} data-testid={`group-member-${c.code.slice(4, 14)}`} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-secure border text-sm transition-colors ${on ? "border-stellar/50 bg-void" : "border-transparent hover:bg-void"}`}>
                <span className="font-branding uppercase tracking-wider">{nameOf(c)}</span>
                <span className={`w-5 h-5 rounded-secure border flex items-center justify-center ${on ? "border-stellar text-stellar" : "border-rim"}`}>{on && <Check size={14} strokeWidth={1.5} />}</span>
              </button>
            );
          })}
        </div>
        <button onClick={submit} disabled={busy || !name.trim() || !picked.length} data-testid="group-create-btn" className="w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-2.5 disabled:opacity-50">
          {busy && <Loader2 size={16} className="animate-spin" />} Create group
        </button>
      </DialogContent>
    </Dialog>
  );
}
